import React, { useRef, useMemo, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Rnd } from 'react-rnd';
import { translate } from '@shakkerai/util-i18n';
import { getTempState, updateTempState, useTempState } from '@shakkerai/pro-store';
import { useEventListener } from 'ahooks';
import { postImageFeedbackSave } from '@shakkerai/services';
import { Checkbox, Textarea } from '@mantine/core';
import { Icon } from '@shakkerai/components';
import { mc } from '@shakkerai/report';
import styles from './feedback.module.scss';
import cs from 'classnames';

const USE_LIKE_STATE = 'USE_LIKE_STATE';

export const ImageFeedback = React.memo(({ element, canvasInteracting }) => {
  const handleMouseOver = useCallback(() => {
    clearTimeout(globalThis.imageMouseLeaveTimer);
    getTempState().updateTempState({ hoveringElement: element.id });
    const localState = JSON.parse(localStorage.getItem(USE_LIKE_STATE) ?? '{}');
    const s = localState[element.id];
    if (s) {
      setStatus(s);
    }
  }, [element.id]);

  const handleMouseOut = useCallback(() => {
    clearTimeout(globalThis.imageMouseLeaveTimer);
    globalThis.imageMouseLeaveTimer = setTimeout(() => {
      getTempState().updateTempState({ hoveringElement: null });
    }, 100);
  }, []);

  useEffect(() => {
    if (!element.id) return;
    const localState = JSON.parse(localStorage.getItem(USE_LIKE_STATE) ?? '{}');
    const s = localState[element.id];
    if (s) {
      setStatus(s);
    }
  }, [element.id]);

  const appendedClassName = canvasInteracting ? 'pointer-events-none' : 'pointer-events-auto';

  const imageComment = element.image_comment || 0;

  // 是否喜欢：喜欢 1 没选择：0 不喜欢：-1
  const [status, setStatus] = useState(imageComment);

  const handleImageCommentClick = (val) => {
    if (status === 0) {
      postImageFeedbackSave({
        outputId: element.innerTaskId,
        imageId: element.url,
        imageComment: val,
      });
      const localState = JSON.parse(localStorage.getItem(USE_LIKE_STATE) ?? '{}');
      localState[element.id] = val;
      localStorage.setItem(USE_LIKE_STATE, JSON.stringify(localState));
      setStatus(val);
      // if (val === 1) {
      //   postImageFeedbackSave({
      //     outputId: element.innerTaskId,
      //     imageId: element.url,
      //     imageComment: 1,
      //   });
      //   const localState = JSON.parse(localStorage.getItem(USE_LIKE_STATE) ?? '{}');
      //   localState[element.id] = 1;
      //   localStorage.setItem(USE_LIKE_STATE, JSON.stringify(localState));
      //   setStatus(1);
      // } else {
      //   updateTempState({
      //     imageFeedbackWindowId: element.id,
      //     selectedFeedbackElement: element,
      //   });
      // }
    } else {
      postImageFeedbackSave({
        outputId: element.innerTaskId,
        imageId: element.url,
        imageComment: 0,
      });
      const localState = JSON.parse(localStorage.getItem(USE_LIKE_STATE) ?? '{}');
      localState[element.id] = 0;
      localStorage.setItem(USE_LIKE_STATE, JSON.stringify(localState));
      setStatus(0);
    }

    mc('tool.canvas.rate', { image_id: element.innerImageId, action: val === 1 ? 'like' : 'hate' });
  };
  return (
    <>
      <div
        onMouseOver={handleMouseOver}
        onMouseLeave={handleMouseOut}
        className={`absolute z-1 left-3 h-7 top-2 px-[12px] flex items-center bg-black/70 rounded-[3px] text-caption-500 text-xs ${appendedClassName}`}
      >
        {/* <span className="text-[#FFF] text-[11px]">{translate('canvas_rate')}</span> */}
        {status !== -1 && (
          <button
            onClick={() => handleImageCommentClick(1)}
            className={`flex items-center justify-center h-7 cursor-pointer font-normal ${
              status !== 1 ? 'mr-[8px]' : ''
            }`}
          >
            <Icon className="shk-great" color={status === 1 ? '#009DFF' : '#fff'} fontSize={16} />
          </button>
        )}
        {status !== 1 && (
          <button
            onClick={() => handleImageCommentClick(-1)}
            className={`flex items-center justify-center cursor-pointer h-7 font-normal`}
          >
            <Icon className="shk-poor" color={status === -1 ? '#009DFF' : '#fff'} fontSize={16} />
          </button>
        )}
      </div>
    </>
  );
});

const initialValues = [
  translate('rate_op1'),
  translate('rate_op2'),
  translate('rate_op3'),
  translate('rate_op4'),
  translate('rate_op5'),
  translate('rate_op6'),
];

export const ImageFeedBackPopover = React.memo(() => {
  const containerRef = useRef(null);
  const imageFeedbackWindowId = useTempState('imageFeedbackWindowId');
  const element = useTempState('selectedFeedbackElement');

  const close = () => {
    updateTempState({
      imageFeedbackWindowId: null,
      selectedFeedbackElement: null,
    });
  };

  const handleGlobalClick = useCallback((event) => {
    if (containerRef.current?.contains(event.target)) {
      return;
    }
    // close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEventListener('click', handleGlobalClick);

  const [comments, setComments] = useState([]);
  const [isOther, setIsOther] = useState(false);

  const handleChange = (commentList) => {
    if (commentList?.some((item) => /其他|Other/i.test(item))) {
      setIsOther(true);
      commentList = commentList.filter((item) => !/其他|Other/i.test(item));
    } else {
      setIsOther(false);
    }
    setComments(commentList);
  };

  const handleFeedChange = (event) => {
    setComments([event.currentTarget.value]);
  };

  const isEmpty = useMemo(() => {
    return comments?.length < 1 || comments?.every((item) => item.label === '');
  }, [comments]);

  const handleSubmit = () => {
    if (isEmpty) {
      return;
    }
    const values = comments?.join(',');
    postImageFeedbackSave({
      outputId: element?.innerTaskId,
      imageId: element?.url,
      imageComment: -1,
      imageCommentInfo: values,
    });
    close();
  };

  if (!imageFeedbackWindowId) {
    return null;
  }

  return createPortal(
    <Rnd scale={1} default={{ x: 200, y: 200 }} className={`z-[103] image-feedback-popup-wrap`}>
      <div
        ref={containerRef}
        className="z-20 w-[300px] bg-white shadow-light rounded-[12px] "
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sub-drag-handle flex items-center justify-between px-[16px] py-[12px] border-b border-b-[#efefef] cursor-move">
          <span className="text-[#222] font-semibold font-grotesk text-sm">{translate('Feedback')}</span>
          <button onClick={close}>
            <Icon fontSize={16} className="icon-border_close-1 text-[#2d2d2d]" />
          </button>
        </div>
        <div className="px-[16px] py-[10px]">
          <h3 className="text-[2d2d2d] text-[12px] font-semibold">{translate('rate_thanks')}</h3>
          <p className="text-[#7f7f7f] text-[12px] mt-[10px]">{translate('rate_question')}</p>
          <div className={cs('mt-[10px]', styles.checkboxGroup)}>
            <Checkbox.Group onChange={handleChange}>
              {initialValues?.map((value, idx) => {
                return (
                  <Checkbox
                    value={value}
                    key={idx}
                    label={value}
                    className="mt-[10px] text-[#2d2d2d]"
                    size="xs"
                    color="#8c8c8c"
                  />
                );
              })}
            </Checkbox.Group>
          </div>
          <div className={cs('mt-[10px]', styles.textInfo)}>
            <Textarea
              disabled={!isOther}
              autosize
              onChange={handleFeedChange}
              placeholder={translate('rate_describe')}
              minRows={4}
            />
          </div>
          <div className="flex justify-end mt-[10px] pt-[15px] pb-[6px] border-t border-[#efefef]">
            <div>
              <span onClick={close} className="text-[#747474] text-[14px] font-medium">
                {translate('Cancel')}
              </span>
              <span
                onClick={handleSubmit}
                className={`ml-[14px] px-[12px] py-[6px] rounded-[4px] ${
                  isEmpty ? 'bg-[#999]' : 'bg-[#000]'
                }  text-[#fff] font-medium`}
              >
                {translate('Submit feedback')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Rnd>,
    document.body,
  );
});
